import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import WifiIcon from '@mui/icons-material/Wifi';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import AccessibleIcon from '@mui/icons-material/Accessible';
import ChildFriendlyIcon from '@mui/icons-material/ChildFriendly';
import PetsIcon from '@mui/icons-material/Pets';
import TvIcon from '@mui/icons-material/Tv';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import SmokingRoomsIcon from '@mui/icons-material/SmokingRooms';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { shopAddAmenities } from '../../middleware/registration_service/updateRequest';
import { useProfileData } from '../../context/ProfileDataContext';
import { useAuth0 } from '@auth0/auth0-react';

const BRAND_COLOR = '#dd8709';

type AlertMessage = {
  type: 'success' | 'error';
  text: string;
} | null;

// Define display names and icons for each amenity
const amenityConfig: {
  [key: string]: { label: string; icon: React.ReactNode };
} = {
  parking: { label: 'Parking', icon: <LocalParkingIcon fontSize="large" /> },
  wifi: { label: 'WiFi', icon: <WifiIcon fontSize="large" /> },
  airConditioning: {
    label: 'Air Conditioning',
    icon: <AcUnitIcon fontSize="large" />,
  },
  wheelchairAccessible: {
    label: 'Wheelchair Accessible',
    icon: <AccessibleIcon fontSize="large" />,
  },
  kidsFriendly: {
    label: 'Kids Friendly',
    icon: <ChildFriendlyIcon fontSize="large" />,
  },
  petFriendly: { label: 'Pet Friendly', icon: <PetsIcon fontSize="large" /> },
  tv: { label: 'TV', icon: <TvIcon fontSize="large" /> },
  magazines: { label: 'Magazines', icon: <MenuBookIcon fontSize="large" /> },
  beverages: { label: 'Beverages', icon: <LocalCafeIcon fontSize="large" /> },
  games: { label: 'Games', icon: <SportsEsportsIcon fontSize="large" /> },
  books: { label: 'Books', icon: <LibraryBooksIcon fontSize="large" /> },
  newspapers: { label: 'Newspapers', icon: <NewspaperIcon fontSize="large" /> },
  music: { label: 'Music', icon: <MusicNoteIcon fontSize="large" /> },
  smokingArea: {
    label: 'Smoking Area',
    icon: <SmokingRoomsIcon fontSize="large" />,
  },
};

// Keep amenityLabels for backward compat
const amenityLabels: { [key: string]: string } = Object.fromEntries(
  Object.entries(amenityConfig).map(([k, v]) => [k, v.label])
);

const availableAmenities = Object.keys(amenityLabels);

// Validation schema
const schema = Yup.object().shape({
  amenities: Yup.object().shape(
    availableAmenities.reduce((acc, amenity) => {
      acc[amenity] = Yup.boolean();
      return acc;
    }, {} as { [key: string]: Yup.BooleanSchema })
  ),
});

export default function ShopAmenities() {
  const [alertMessage, setAlertMessage] = useState<AlertMessage>(null);
  const [loading, setLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const { shop, setShop, loading: initialLoading } = useProfileData();
  const { getAccessTokenSilently } = useAuth0();
  const shopId = shop?.id ?? BigInt(0);

  const formik = useFormik({
    initialValues: {
      amenities: availableAmenities.reduce((acc, amenity) => {
        acc[amenity] = false;
        return acc;
      }, {} as { [key: string]: boolean }),
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      setLoading(true);
      setAlertMessage(null);

      try {
        const token = await getAccessTokenSilently();
        await shopAddAmenities(shopId, values.amenities, token);
        setIsDirty(false);
        setShop((prev) =>
          prev
            ? {
                ...prev,
                amenities: {
                  ...prev.amenities,
                  ...values.amenities,
                  id: prev.amenities?.id ?? shopId,
                },
              }
            : prev
        );
        setAlertMessage({
          type: 'success',
          text: 'Amenities updated successfully!',
        });
        setTimeout(() => {
          setAlertMessage(null);
        }, 3000);
      } catch (error) {
        console.error('Failed to update amenities:', error);
        setAlertMessage({ type: 'error', text: 'Failed to update amenities.' });
      } finally {
        setLoading(false);
      }
    },
  });

  // Seed formik values from the shop amenities already loaded by ProfileDataProvider
  useEffect(() => {
    if (shop?.amenities) {
      formik.setFieldValue('amenities', {
        ...formik.values.amenities,
        ...shop.amenities,
      });
    }
  }, [shop?.amenities]);

  const handleCheckboxChange = (amenity: string) => {
    formik.setFieldValue(
      `amenities.${amenity}`,
      !formik.values.amenities[amenity]
    );
    setIsDirty(true);
  };

  if (initialLoading) {
    return (
      <Container sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading amenities...</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ paddingBottom: '30px !important', paddingTop: '5px' }}>
      <form onSubmit={formik.handleSubmit}>
        <Grid container alignItems="center" justifyContent="flex-end">
          <Grid item>
            <Button
              className="buttonHover"
              type="submit"
              disabled={loading || !isDirty}
              sx={{
                p: 1.5,
                bgcolor: '#dd8709',
                mt: 2,
                mb: 1,
                color: 'white',
                fontFamily: 'GFS Didot',
                fontWeight: 'bold',
                '&:hover': { bgcolor: '#b86e08' },
                '&.Mui-disabled': { bgcolor: '#e0b96a', color: '#fff' },
              }}
              variant="contained"
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Save Amenities'
              )}
            </Button>
          </Grid>
        </Grid>

        {alertMessage && (
          <Alert severity={alertMessage.type} sx={{ mt: 2, mb: 2 }}>
            {alertMessage.text}
          </Alert>
        )}

        <Grid container spacing={2}>
          {availableAmenities.map((amenity) => {
            const enabled = formik.values.amenities[amenity] || false;
            return (
              <Grid item xs={6} sm={4} md={3} key={amenity}>
                <Tooltip
                  title={enabled ? 'Click to disable' : 'Click to enable'}
                  arrow
                >
                  <Box
                    role={'checkbox'}
                    aria-checked={enabled}
                    tabIndex={0}
                    onClick={() => handleCheckboxChange(amenity)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCheckboxChange(amenity);
                      }
                    }}
                    sx={{
                      position: 'relative',
                      height: '140px',
                      width: '100%',
                      cursor: 'pointer',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      border: enabled
                        ? `2px solid ${BRAND_COLOR}`
                        : '2px solid rgba(200,200,200,0.4)',
                      background: enabled
                        ? `linear-gradient(145deg, #f5d28a 0%, #f9e0a8 55%, #faebbe 100%)`
                        : 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 60%, #e8e8e8 100%)',
                      boxShadow: enabled
                        ? `0 8px 24px rgba(221,135,9,0.2), inset 0 1px 0 rgba(255,255,255,0.6)`
                        : '0 4px 16px rgba(0,0,0,0.10), inset 0 1px 0 rgba(255,255,255,0.9)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 1,
                      transition: 'all 0.25s ease',
                      userSelect: 'none',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: enabled
                          ? `0 12px 32px rgba(221,135,9,0.25), inset 0 1px 0 rgba(255,255,255,0.7)`
                          : '0 8px 24px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.9)',
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '50%',
                        background:
                          'linear-gradient(180deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0) 100%)',
                        borderRadius: '12px 12px 0 0',
                        pointerEvents: 'none',
                      },
                    }}
                  >
                    {/* Check badge */}
                    {enabled && (
                      <CheckCircleIcon
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          fontSize: '18px',
                          color: BRAND_COLOR,
                          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.15))',
                        }}
                      />
                    )}

                    {/* Icon */}
                    <Box
                      sx={{
                        color: enabled ? BRAND_COLOR : '#999',
                        filter: enabled
                          ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                          : 'none',
                        transition: 'color 0.25s ease',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {amenityConfig[amenity]?.icon}
                    </Box>

                    {/* Label */}
                    <Typography
                      component="span"
                      sx={{
                        fontSize: '12px',
                        fontWeight: 'bold',
                        fontFamily: 'GFS Didot',
                        color: enabled ? '#7a4a00' : '#555',
                        textAlign: 'center',
                        px: 1,
                        textShadow: 'none',
                        transition: 'color 0.25s ease',
                      }}
                    >
                      {amenityConfig[amenity]?.label || amenity}
                    </Typography>
                  </Box>
                </Tooltip>
              </Grid>
            );
          })}
        </Grid>
      </form>
    </Container>
  );
}
